import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { getLocalSEOAgentReport, runLocalSEOAgentReport } from "src/common/Scraper"
import { LocalSEOAgentReport, LocalSEOAgentRunPayload } from "../types"
import useUpdateUserAgents from "../../hooks/useUpdateUserAgents";
import { sleep } from "src/common/config/FunctionHelper";
import { useState } from "react";
import { posthogService, PostHogEvent } from "src/common/services/posthogService";

export const useGetLocalSEOReport = ({ userId, siteId, accountId, locationId }: { userId: string, siteId: string, accountId: string, locationId: string }) => {
  const isParamsValid =
    !!userId &&
    userId !== 'undefined' &&
    userId !== 'null' &&
    !!accountId &&
    accountId !== 'undefined' &&
    accountId !== 'null' &&
    accountId !== '' &&
    !!locationId &&
    locationId !== 'undefined' &&
    locationId !== 'null' &&
    locationId !== '';


  return useQuery<LocalSEOAgentReport>({
    queryKey: ["getLocalSEOReport", accountId, locationId, siteId],
    queryFn: isParamsValid
      ? () => {
        return getLocalSEOAgentReport(userId, siteId, accountId, locationId);
      }
      : () => Promise.resolve({} as LocalSEOAgentReport),
    enabled: isParamsValid,
  })
}

interface UseRunLocalSEOAgent {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onMutateStart?: () => void;
}

interface HandleLocalSEORunProps {
  account_id: string;
  location_id: string;
  local_keywords: string[];
}

export const useRunLocalSEOAgent = ({ onSuccess, onError, onMutateStart }: UseRunLocalSEOAgent = {}) => {
  const queryClient = useQueryClient()
  const { mutate: updateAgents } = useUpdateUserAgents('local-seo');

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const localSEORunMutation = useMutation({
    mutationFn: (payload: LocalSEOAgentRunPayload) => runLocalSEOAgentReport(payload),
    mutationKey: ["runLocalSEOAgent"],
    onSuccess: (data, variables) => {
      // Track Orbit AI Local SEO agent run in PostHog
      // Captures site ID, user ID, GBP account ID, location ID, and count of local keywords
      posthogService.track(PostHogEvent.ORBIT_AI_LOCAL_SEO_RUN, {
        site_id: variables?.site_id,
        user_id: variables?.user_id,
        account_id: variables?.account_id,
        location_id: variables?.location_id,
        local_keywords_count: variables?.local_keywords?.length || 0
      });

      queryClient.invalidateQueries({
        queryKey: ["getLocalSEOReport"]
      })
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error, variables) => {
      if (onError) {
        onError(error);
      }
      updateLocalSEOAgentStatus(false, variables.account_id, variables.location_id, variables.site_id, 'local_seo_dashboard')
    }
  })


  function updateLocalSEOAgentStatus(status: boolean, account_id: string, location_id: string, overrideSiteId?: string, source: string = 'local_seo_dashboard') {
    updateAgents({
      site_id: overrideSiteId,
      source,
      agents: [
        {
          name: 'local_seo',
          enabled: status,
          config: {
            account_id,
            location_id,
            local_keywords: ['']
          }
        }
      ]
    });
  }


  async function handleLocalSEORun({ account_id, location_id, local_keywords }: HandleLocalSEORunProps, overrides: { siteId: string; userId: string, shouldRun?: boolean }) {
    const effectiveUserId = overrides?.userId;
    const effectiveSiteId = overrides?.siteId;
    const shouldRun = overrides?.shouldRun ?? true;

    if (onMutateStart) {
      onMutateStart();
    }

    setTimeout(() => {
      setDialogOpen(false);
    }, 2000);

    if(shouldRun){
      localSEORunMutation.mutate({
        user_id: effectiveUserId || '',
        site_id: effectiveSiteId || '',
        account_id,
        location_id,
        local_keywords
      })
    }

    await sleep(2000);

    updateLocalSEOAgentStatus(true, account_id, location_id, overrides?.siteId)

  }


  return {
    updateLocalSEOAgentStatus,
    handleLocalSEORun,
    localSEORunMutation,
    setDialogOpen,
    dialogOpen
  }
}

